import CustomLoadingButton from 'Components/CustomLoadingButton'
import StyledLabel from 'Components/StyledLabel'
import TranslationInput from 'Components/TranslationInput'
import { SELECTED_ZONE_PARAMETER_KEY } from 'Services/ControlDataManagement/Features/Zone/Hooks/useZonesWithParams'
import type { Zone } from 'Services/ControlDataManagement/Features/Zone/types'
import {
  useCreateSingleZoneMutation,
  useUpdateSingleZoneMutation
} from 'Services/ControlDataManagement/Features/Zone/zoneApi'
import type { FieldValidationErrors } from 'Services/ControlDataManagement/types'
import type { Translation } from 'Store'
import type { ControlDataServiceError } from 'Util/ApiClient'
import { useValidationErrors } from 'Util/hooks/useValidationErrors'
import SnackbarUtils from 'Util/Snackbar'
import { appendQueryParamsToUrl } from 'Util/url'
import { Box, FormControl, FormHelperText, Grid, TextField, Typography } from '@mui/material'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

const fieldErrorsDefaultState: FieldValidationErrors = {
  zone_id: [],
  codespace_id: [],
  translations: []
}

interface ZoneFormProps {
  zone?: Zone
  successBaseUrl: string
}

export default function ZoneForm({ zone, successBaseUrl }: Readonly<ZoneFormProps>) {
  const navigate = useNavigate()
  const isEdit = !!zone
  const [zoneDetails, setZoneDetails] = useState<Partial<Zone>>((isEdit && zone) || {})

  const { fieldErrors, setValidationErrors, getFieldErrorHelperText } =
    useValidationErrors(fieldErrorsDefaultState)

  const [createSingleZone, { isLoading: isCreateLoading }] = useCreateSingleZoneMutation()
  const [updateSingleZone, { isLoading: isUpdateLoading }] = useUpdateSingleZoneMutation()

  const handleChange = (
    value: string | string[] | undefined | Translation[],
    field: keyof Zone
  ) => {
    if (value !== undefined) {
      setZoneDetails(prevState => ({ ...prevState, [field]: value }) as Zone)
    }
  }

  const handleSetTranslations = (translations: Translation[]) => {
    handleChange(translations, 'translations')
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (zone) {
      updateSingleZone({ zone_id: zone.zone_id, ...zoneDetails })
        .unwrap()
        .then(() => {
          SnackbarUtils.success(t`Zone updated successfully.`)
          navigate(successBaseUrl)
        })
        .catch(({ data: errorResponse }: { data: ControlDataServiceError }) => {
          const { violations } = errorResponse
          setValidationErrors(violations)
          SnackbarUtils.error(t`There was an error updating the zone.`)
        })
      return
    }

    createSingleZone(zoneDetails)
      .unwrap()
      .then((zoneResponse: Zone) => {
        SnackbarUtils.success(t`Zone created successfully.`)
        navigate(
          appendQueryParamsToUrl(successBaseUrl, {
            [SELECTED_ZONE_PARAMETER_KEY]: zoneResponse.zone_id
          })
        )
      })
      .catch(({ data: errorResponse }: { data: ControlDataServiceError }) => {
        const { violations } = errorResponse
        setValidationErrors(violations)
        SnackbarUtils.error(t`There was an error creating the zone.`)
      })
  }

  const isLoading = isCreateLoading || isUpdateLoading

  return (
    <Grid container spacing={2} mb={4}>
      <Grid size={12}>
        <Box component="form" onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', mb: 4 }}>
            <FormControl sx={{ minWidth: 240, mr: 3 }}>
              <TextField
                name="zone_id"
                required
                label={t`Zone identifier`}
                size="small"
                value={zoneDetails?.zone_id ?? ''}
                slotProps={{ htmlInput: { maxLength: 40 } }}
                onChange={e => handleChange(e.target.value, 'zone_id')}
                error={fieldErrors.zone_id.length > 0}
                helperText={getFieldErrorHelperText('zone_id')}
              />
            </FormControl>
            <FormControl sx={{ minWidth: 240, mr: 3 }}>
              <TextField
                name="codespace_id"
                required
                label={t`Codespace identifier`}
                size="small"
                value={zoneDetails?.codespace_id ?? ''}
                slotProps={{ htmlInput: { maxLength: 40 } }}
                onChange={e => handleChange(e.target.value, 'codespace_id')}
                error={fieldErrors.codespace_id.length > 0}
                helperText={getFieldErrorHelperText('codespace_id')}
              />
            </FormControl>
          </Box>
          <StyledLabel>{t`Translations`}</StyledLabel>
          <Box sx={{ display: 'flex', mb: 4 }}>
            <FormControl
              sx={{ minWidth: 300, mr: 2, mt: 2, display: 'flex', gap: 2 }}
              size="small"
              required
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={18} sx={{ mr: 1, minWidth: 30 }} color="text.secondary">
                  FI
                </Typography>
                <TranslationInput
                  translations={zoneDetails?.translations ?? []}
                  field="value"
                  label={t`Value`}
                  locale={'fi'}
                  setTranslations={handleSetTranslations}
                  size="small"
                  error={fieldErrors.translations.length > 0}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={18} sx={{ mr: 1, minWidth: 30 }} color="text.secondary">
                  SV
                </Typography>
                <TranslationInput
                  translations={zoneDetails?.translations ?? []}
                  field="value"
                  label={t`Value`}
                  locale={'sv'}
                  setTranslations={handleSetTranslations}
                  size="small"
                  error={fieldErrors.translations.length > 0}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={18} sx={{ mr: 1, minWidth: 30 }} color="text.secondary">
                  EN
                </Typography>
                <TranslationInput
                  translations={zoneDetails?.translations ?? []}
                  field="value"
                  label={t`Value`}
                  locale={'en'}
                  setTranslations={handleSetTranslations}
                  size="small"
                  error={fieldErrors.translations.length > 0}
                />
              </Box>
              <FormHelperText error={fieldErrors.translations.length > 0}>
                {getFieldErrorHelperText('translations')}
              </FormHelperText>
            </FormControl>
          </Box>
          <CustomLoadingButton
            type="submit"
            size="large"
            isLoading={isLoading}
            iconPosition="end"
          >{t`Save zone`}</CustomLoadingButton>
        </Box>
      </Grid>
    </Grid>
  )
}
